import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './PromoteTransport.css';

const SearchDropdown = ({ placeholder, options, value, onChange, disabled }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => String(o.id) === String(value));
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={`student-transport-promotion-search-dropdown ${disabled ? 'disabled' : ''}`} ref={ref}>
      <div className="student-transport-promotion-search-dropdown-box" onClick={() => !disabled && setOpen(v => !v)}>
        <span style={{ color: selected ? '#1e293b' : '#94a3b8' }}>{selected ? selected.label : placeholder}</span>
        <FaChevronDown style={{ fontSize: '0.8rem', color: '#94a3b8', transition: '0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </div>
      {open && (
        <div className="student-transport-promotion-search-dropdown-list">
          <div className="student-transport-promotion-search-dropdown-input-wrapper">
            <FaSearch />
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          </div>
          <ul>
            <li onClick={() => { onChange(''); setOpen(false); setSearch(''); }}>-- Clear --</li>
            {filtered.map(o => (
              <li 
                key={o.id} 
                className={`${String(o.id) === String(value) ? 'student-transport-promotion-selected-li' : ''} ${o.disabled ? 'disabled-option' : ''}`}
                onClick={() => { 
                  if (!o.disabled) { onChange(o.id); setOpen(false); setSearch(''); } 
                }}
              >
                {o.label} {o.disabled && <span className="student-transport-promotion-dropdown-full-badge">FULL</span>}
              </li>
            ))}
            {filtered.length === 0 && <li className="empty-li">No results found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Cancel Modal Component ────────────────────────────────────────────────────
const CancelModal = ({ alloc, onClose, onDone }) => {
  const [tab, setTab]           = useState('this_year'); // 'this_year' | 'next_year'
  const [cancelDate, setCancelDate] = useState(new Date().toISOString().split('T')[0]);
  const [nextSession, setNextSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const joinDate = alloc.joinDate ? alloc.joinDate.split('T')[0] : '';
  const endDate  = alloc.endDate  ? alloc.endDate.split('T')[0]  : '';

  // Load next session when tab switches to next_year
  useEffect(() => {
    if (tab !== 'next_year') return;
    setLoadingSession(true);
    setError('');
    const colId = alloc.college?._id || alloc.college;
    const currentName = alloc.session?.name || '';
    api.get('/sessions', { params: { college: colId } })
      .then(r => {
        const all = r.data.data || [];
        const m = currentName.match(/(\d{4})\D*(\d{2,4})/);
        if (!m) { setError('Cannot parse session name'); setLoadingSession(false); return; }
        let sy = parseInt(m[1]), ey = parseInt(m[2]);
        if (ey < 100) ey = sy + 1;
        const nsy = sy + 1, ney = ey + 1;
        const patterns = [
          `${nsy}-${String(ney).slice(-2)}`,
          `${nsy}-${ney}`,
          `${nsy}/${String(ney).slice(-2)}`,
          `${nsy}/${ney}`,
        ];
        const found = patterns.reduce((acc, p) => acc || all.find(s => s.name === p), null);
        if (found) setNextSession(found);
        else setError(`Next session not found. Please create it first.`);
      })
      .catch(() => setError('Failed to load sessions'))
      .finally(() => setLoadingSession(false));
  }, [tab, alloc]);

  const handleConfirm = async () => {
    setError('');
    if (tab === 'this_year' && !cancelDate) {
      setError('Cancel date is required'); return;
    }
    if (tab === 'next_year' && !nextSession) {
      setError('Next session not found'); return;
    }
    setSaving(true);
    try {
      const payload = { cancelType: tab === 'this_year' ? 'current' : 'next' };
      if (tab === 'this_year') payload.cancelDate = cancelDate;
      await api.post(`/transport/allocations/${alloc._id}/cancel`, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tp-cancel-modal-overlay" onClick={onClose}>
      <div className="tp-cancel-modal" onClick={e => e.stopPropagation()}>
        <div className="tp-cancel-modal-header">
          <span>Cancel Transport — {alloc.student?.name || '—'}</span>
          <button className="tp-cancel-modal-close" onClick={onClose}><FaTimes /></button>
        </div>

        {/* Tabs */}
        <div className="tp-cancel-tabs">
          <button
            className={`tp-cancel-tab ${tab === 'this_year' ? 'active' : ''}`}
            onClick={() => { setTab('this_year'); setError(''); }}
          >
            Cancel This Year
          </button>
          <button
            className={`tp-cancel-tab ${tab === 'next_year' ? 'active' : ''}`}
            onClick={() => { setTab('next_year'); setError(''); }}
          >
            Cancel Next Year
          </button>
        </div>

        <div className="tp-cancel-modal-body">
          {tab === 'this_year' && (
            <>
              <div className="tp-cancel-info-row">
                <span className="tp-cancel-info-label">Session</span>
                <span className="tp-cancel-info-value">{alloc.session?.name || '—'}</span>
              </div>
              <div className="tp-cancel-info-row">
                <span className="tp-cancel-info-label">Join Date</span>
                <span className="tp-cancel-info-value">{joinDate ? new Date(joinDate).toLocaleDateString('en-GB') : '—'}</span>
              </div>
              <div className="tp-cancel-info-row">
                <span className="tp-cancel-info-label">End Date</span>
                <span className="tp-cancel-info-value">{endDate ? new Date(endDate).toLocaleDateString('en-GB') : '—'}</span>
              </div>
              <div className="tp-cancel-field">
                <label className="tp-cancel-field-label">Cancel Date <span style={{color:'#ef4444'}}>*</span></label>
                <input
                  type="date"
                  className="tp-cancel-date-input"
                  value={cancelDate}
                  min={joinDate}
                  max={endDate}
                  onChange={e => setCancelDate(e.target.value)}
                />
                <span className="tp-cancel-field-hint">Session status will become <strong>Cancelled</strong></span>
              </div>
            </>
          )}

          {tab === 'next_year' && (
            <>
              {loadingSession ? (
                <div className="tp-cancel-loading">Loading next session...</div>
              ) : (
                <>
                  <div className="tp-cancel-session-flow">
                    <div className="tp-cancel-session-box current">
                      <span className="tp-cancel-session-label">Current Session</span>
                      <span className="tp-cancel-session-name">{alloc.session?.name || '—'}</span>
                      <span className="tp-cancel-session-status completed">→ Completed</span>
                    </div>
                    <div className="tp-cancel-session-arrow">→</div>
                    <div className="tp-cancel-session-box next">
                      <span className="tp-cancel-session-label">Next Session</span>
                      <span className="tp-cancel-session-name">{nextSession?.name || '—'}</span>
                      <span className="tp-cancel-session-status inactive">→ Inactive</span>
                    </div>
                  </div>
                  <div className="tp-cancel-note">
                    Current session <strong>{alloc.session?.name}</strong> will be marked as <strong>Completed</strong>.
                    A new entry for <strong>{nextSession?.name || '...'}</strong> will be added with status <strong>Inactive</strong> (no join/end date).
                  </div>
                </>
              )}
            </>
          )}

          {error && <div className="tp-cancel-error">{error}</div>}
        </div>

        <div className="tp-cancel-modal-footer">
          <button className="tp-cancel-btn-back" onClick={onClose} disabled={saving}>Back</button>
          <button
            className="tp-cancel-btn-confirm"
            onClick={handleConfirm}
            disabled={saving || (tab === 'next_year' && (!nextSession || loadingSession))}
          >
            {saving ? 'Processing...' : 'Confirm Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PromoteTransport = () => {
  const { user }  = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const isScoped  = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [allocations, setAllocations]     = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  
  // Selection State
  const [selectedIds, setSelectedIds]     = useState([]);

  // Filter Dropdown status (default 'all')
  const [statusTab, setStatusTab]         = useState('all');
  const [statusFilter, setStatusFilter]   = useState('all'); // 'all'|'lt30'|'lt7'|'lt3'|'today'|'expired'
  
  // Table Filters
  const [searchQuery, setSearchQuery]     = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterBatch, setFilterBatch]     = useState('');
  const [filterClass, setFilterClass]     = useState('');

  const [showPromoteForm, setShowPromoteForm] = useState(false);
  const [editId, setEditId]                   = useState(null);
  const [expandedRow, setExpandedRow]         = useState(null);
  const [promoteAlloc, setPromoteAlloc]       = useState(null);
  const [cancelModalAlloc, setCancelModalAlloc] = useState(null);

  // Form fields
  const [formCollege, setFormCollege]   = useState('');
  const [formSession, setFormSession]   = useState('');
  const [formSessionName, setFormSessionName] = useState('');
  const [formStudent, setFormStudent]   = useState('');
  const [formBatch, setFormBatch]       = useState('');
  const [formClass, setFormClass]       = useState('');
  const [formBus, setFormBus]           = useState('');
  const [formRoute, setFormRoute]       = useState('');
  const [formStop, setFormStop]         = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState('');
  
  // Dates
  const [formJoinDate, setFormJoinDate] = useState('');
  const [formEndDate, setFormEndDate]   = useState('');

  // Data
  const [activeSession, setActiveSession] = useState(null);
  const [activeSessionsByCollege, setActiveSessionsByCollege] = useState({}); // Map of college ID to active session
  const [allStudents, setAllStudents]     = useState([]);
  const [busesData, setBusesData]         = useState([]);
  const [routesData, setRoutesData]       = useState([]);

  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setFormCollege(String(colleges[0]._id));
    else if (colleges.length === 1)      setFormCollege(String(colleges[0]._id));
  }, [colleges, isAdmin]);

  const loadAllocations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/transport/allocations', { params: { status: 'active' } });
      const allocsData = data.data || [];
      setAllocations(allocsData);
      
      // Load active sessions for each college
      const collegeIds = [...new Set(allocsData.map(a => String(a.college?._id || a.college)))];
      const sessionMap = {};
      
      for (const colId of collegeIds) {
        try {
          const sessResp = await api.get('/sessions', { params: { college: colId } });
          const activeSess = (sessResp.data.data || []).find(s => s.isActive);
          if (activeSess) sessionMap[colId] = activeSess;
        } catch (e) {
          // ignore
        }
      }
      setActiveSessionsByCollege(sessionMap);
    } catch { 
      setAllocations([]); 
      setActiveSessionsByCollege({});
    }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAllocations(); }, []);

  useEffect(() => {
    if (!formCollege) {
      setActiveSession(null); setAllStudents([]); setBusesData([]); setRoutesData([]);
      if (!editId) { setFormSession(''); setFormSessionName(''); setFormStudent(''); setFormBatch(''); setFormClass(''); }
      return;
    }
    api.get('/sessions', { params: { college: formCollege } })
      .then(r => {
        const active = (r.data.data || []).find(s => s.isActive);
        setActiveSession(active || null);
        if (!editId) {
          if (active) { setFormSession(active._id); setFormSessionName(active.name); }
          else        { setFormSession(''); setFormSessionName(''); }
        }
      }).catch(() => { setActiveSession(null); if (!editId) { setFormSession(''); setFormSessionName(''); } });

    api.get('/students', { params: { college: formCollege, limit: 1000 } })
      .then(r => setAllStudents(r.data.data || [])).catch(() => setAllStudents([]));

    api.get('/transport/buses', { params: { college: formCollege } })
      .then(r => setBusesData(r.data.data || [])).catch(() => setBusesData([]));

    api.get('/transport/routes', { params: { activeOnly: 'true' } })
      .then(r => setRoutesData(r.data.data || [])).catch(() => setRoutesData([]));
  }, [formCollege, editId]);

  // Date Logic
  useEffect(() => {
    if (formJoinDate) {
      const date = new Date(formJoinDate);
      date.setFullYear(date.getFullYear() + 1);
      date.setDate(date.getDate() - 1);
      setFormEndDate(date.toISOString().split('T')[0]);
    } else {
      setFormEndDate('');
    }
  }, [formJoinDate]);

  useEffect(() => {
    if (!formStudent || editId) return;
    const s = allStudents.find(x => x._id === formStudent);
    if (s) {
      if (s.admissionYear) setFormBatch(s.admissionYear);
      const clsId = s.class?._id || s.class || '';
      if (clsId) setFormClass(String(clsId));
    }
  }, [formStudent, allStudents, editId]);

  useEffect(() => {
    if (formRoute && formStop) {
      const route = routesData.find(r => r._id === formRoute);
      const stop  = route?.stops?.find(s => s.name === formStop);
      setCalculatedPrice(stop?.price || '');
    } else {
      setCalculatedPrice('');
    }
  }, [formRoute, formStop, routesData]);

  const filteredStudents = useMemo(() => {
    if (!formSession) return allStudents;
    return allStudents.filter(s => String(s.session?._id || s.session) === formSession);
  }, [allStudents, formSession]);

  const batchOptions = useMemo(() => {
    const batches = [...new Set(filteredStudents.map(s => s.admissionYear).filter(Boolean))].sort();
    return batches.map(b => ({ id: b, label: `Batch ${b}` }));
  }, [filteredStudents]);

  const classOptions = useMemo(() => {
    const map = new Map();
    filteredStudents.forEach(s => {
      const id   = s.class?._id || s.class || '';
      const name = s.class?.name || s.class || '';
      const medium = s.medium || s.class?.medium || '';
      const label = medium ? `${name} (${medium})` : name;
      if (id && label) map.set(id, label);
    });
    return Array.from(map, ([id, label]) => ({ id, label }));
  }, [filteredStudents]);

  const displayedStudents = useMemo(() => {
    let list = filteredStudents;
    if (formBatch) list = list.filter(s => s.admissionYear === formBatch);
    if (formClass) list = list.filter(s => String(s.class?._id || s.class) === formClass || s.class?.name === formClass);
    return list;
  }, [filteredStudents, formBatch, formClass]);

  const allocatedStudentIds = useMemo(() => {
    if (!formSession) return new Set();
    return new Set(
      allocations
        .filter(a => String(a.session?._id || a.session) === formSession && !editId)
        .map(a => String(a.student?._id || a.student))
    );
  }, [allocations, formSession, editId]);

  const studentOptions = useMemo(() =>
    displayedStudents
      .filter(s => !allocatedStudentIds.has(String(s._id)))
      .map(s => ({ id: s._id, label: `${s.name} (${s.admissionNo || 'N/A'})` }))
  , [displayedStudents, allocatedStudentIds]);
  
  const busOptions = useMemo(() => {
    return busesData.map(b => {
      const allocatedCount = allocations.filter(a => String(a.bus?._id || a.bus) === String(b._id)).length;
      const isFull = allocatedCount >= b.capacity;
      const isCurrentlyAssignedBus = editId && String(formBus) === String(b._id);
      return { 
        id: b._id, 
        label: `${b.busNumber} (${Math.max(0, b.capacity - allocatedCount)} seats left)`, 
        disabled: isFull && !isCurrentlyAssignedBus
      };
    });
  }, [busesData, allocations, editId, formBus]);

  const routeOptions = useMemo(() => routesData.map(r => ({ id: r._id, label: r.name })), [routesData]);

  const stopOptions = useMemo(() => {
    const route = routesData.find(r => String(r._id) === String(formRoute));
    return (route?.stops || []).map(s => ({ id: s.name, label: s.name }));
  }, [routesData, formRoute]);

  useEffect(() => {
    if (!formBus) return;
    const bus = busesData.find(b => String(b._id) === String(formBus));
    if (bus?.route) {
      const routeId = String(bus.route?._id || bus.route);
      if (formRoute !== routeId) {
        setFormRoute(routeId);
        setFormStop('');
      }
    }
  }, [formBus, busesData]);

  // ── Helper function to find next session by name pattern ──────────────────
  const findNextSessionByName = (currentSessionName, allSessions) => {
    if (!currentSessionName || !allSessions || allSessions.length === 0) return null;
    const yearMatch = currentSessionName.match(/(\d{4})\D*(\d{2,4})/);
    if (!yearMatch) return null;
    let startYear = parseInt(yearMatch[1]);
    let endYear = parseInt(yearMatch[2]);
    if (endYear < 100) endYear = startYear + 1;
    const nextStartYear = startYear + 1;
    const nextEndYear = endYear + 1;
    const patterns = [
      `${nextStartYear}-${String(nextEndYear).slice(-2)}`,
      `${nextStartYear}-${nextEndYear}`,
      `${nextStartYear}/${String(nextEndYear).slice(-2)}`,
      `${nextStartYear}/${nextEndYear}`,
      `${String(nextStartYear).slice(-2)}-${String(nextEndYear).slice(-2)}`,
    ];
    for (const pattern of patterns) {
      const found = allSessions.find(s => s.name === pattern);
      if (found) return found;
    }
    return null;
  };

  const tableFilterOptions = useMemo(() => {
    let filtered = allocations;
    if (filterCollege) filtered = filtered.filter(a => String(a.college?._id || a.college) === filterCollege);
    return {
      sessions: [...new Map(filtered.map(a => [String(a.session?._id || a.session), a.session?.name || a.session]).filter(([id]) => id && id !== 'undefined')).entries()].map(([id, name]) => ({ id, name })),
      batches:  [...new Set(filtered.map(a => a.batch).filter(Boolean))].sort(),
      classes:  [...new Map(filtered.map(a => [String(a.class?._id || a.class), a.class?.name || a.class]).filter(([id]) => id && id !== 'undefined')).entries()].map(([id, name]) => ({ id, name }))
    };
  }, [allocations, filterCollege]);

  const resetForm = () => {
    const col = colleges.length === 1 ? colleges[0]._id : (isScoped ? formCollege : '');
    setFormCollege(col); setFormSession(''); setFormSessionName('');
    setFormStudent(''); setFormBatch(''); setFormClass('');
    setFormBus(''); setFormRoute(''); setFormStop(''); setCalculatedPrice('');
    setFormJoinDate(''); setFormEndDate('');
    setEditId(null); setPromoteAlloc(null); setShowPromoteForm(false);
  };

  const handlePromoteSubmit = async () => {
    if (!formCollege || !formStudent || !formBus || !formRoute || !formStop || !formJoinDate || !formEndDate) {
      Swal.fire('Missing Details', 'Please fill all required fields.', 'warning'); return;
    }
    setSaving(true);
    try {
      const selectedStudent = allStudents.find(x => x._id === formStudent);
      const studentClassId  = selectedStudent?.class?._id || selectedStudent?.class || formClass || undefined;

      const payload = {
        college: formCollege,
        session: formSession, 
        batch:   formBatch || selectedStudent?.admissionYear || '',
        class:   studentClassId,
        student: formStudent,
        bus:     formBus,
        route:   formRoute,
        stop:    formStop,
        price:   calculatedPrice,
        joinDate: formJoinDate,
        endDate:  formEndDate,
      };

      await api.put(`/transport/allocations/${editId}`, payload);
      Swal.fire('Promoted', 'Transport successfully promoted to the selected duration.', 'success');
      
      resetForm(); loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to promote', 'error');
    } finally { setSaving(false); }
  };

  const handleCancel = (alloc) => setCancelModalAlloc(alloc);

  // Bulk Handlers
  const handleBulkPromoteClick = async () => {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: '🔄 Bulk Promote Transports',
      html: `<div style="text-align:left;font-size:0.92rem;line-height:1.8">
        <p>Promote <strong>${selectedIds.length}</strong> selected transports to next session?</p>
        <p style="color:#64748b;font-size:0.85rem">• Old session → <strong>Completed</strong><br/>• New session dates: old endDate+1 day as joinDate, +1 year as endDate<br/>• Bus, route, stop remain the same</p>
      </div>`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Promote Selected',
      cancelButtonText: 'Cancel',
      width: 480,
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    Swal.fire({ title: 'Promoting...', text: 'Please wait...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const allocsToPromote = allocations.filter(a => selectedIds.includes(a._id));
      const sessionsCache = {};

      for (let alloc of allocsToPromote) {
        const colId = alloc.college?._id || alloc.college;
        const currentSessionName = alloc.session?.name || '';

        // Auto-calculate new dates: joinDate = old endDate + 1 day, endDate = joinDate + 1 year - 1 day
        let newJoinDate, newEndDate;
        if (alloc.endDate) {
          const end = new Date(alloc.endDate);
          end.setDate(end.getDate() + 1);
          newJoinDate = end.toISOString().split('T')[0];
          const endD = new Date(newJoinDate);
          endD.setFullYear(endD.getFullYear() + 1);
          endD.setDate(endD.getDate() - 1);
          newEndDate = endD.toISOString().split('T')[0];
        } else {
          newJoinDate = new Date().toISOString().split('T')[0];
          const endD = new Date(newJoinDate);
          endD.setFullYear(endD.getFullYear() + 1);
          endD.setDate(endD.getDate() - 1);
          newEndDate = endD.toISOString().split('T')[0];
        }

        let nextSessId = sessionsCache[colId];
        if (!nextSessId) {
          try {
            const r = await api.get('/sessions', { params: { college: colId } });
            const allSessions = r.data.data || [];
            const nextSession = findNextSessionByName(currentSessionName, allSessions);
            if (!nextSession) {
              Swal.fire('Next Session Not Found', `Cannot find next session for "${currentSessionName}". Please create it first.`, 'warning');
              setSaving(false); return;
            }
            nextSessId = nextSession._id;
            sessionsCache[colId] = nextSessId;
          } catch (err) {
            Swal.fire('Error', 'Failed to fetch sessions: ' + (err.response?.data?.message || err.message), 'error');
            setSaving(false); return;
          }
        }

        // 1. Mark old allocation as completed
        await api.put(`/transport/allocations/${alloc._id}`, { status: 'completed', isActive: false });

        // 2. Create new allocation for next session
        await api.post('/transport/allocations', {
          college:  colId,
          session:  nextSessId,
          batch:    alloc.batch || '',
          class:    alloc.class?._id || alloc.class,
          student:  alloc.student?._id || alloc.student,
          bus:      alloc.bus?._id || alloc.bus,
          route:    alloc.route?._id || alloc.route,
          stop:     alloc.stop,
          price:    alloc.price,
          joinDate: newJoinDate,
          endDate:  newEndDate,
          status:   'active',
          isActive: true,
        });
      }

      Swal.fire('Success', 'Selected transports promoted to next session.', 'success');
      setSelectedIds([]);
      loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to promote some transports', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkCancelClick = async () => {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Cancel Transports',
      html: `Are you sure you want to cancel <strong>${selectedIds.length}</strong> selected transports? They will be deactivated.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Cancel All',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    Swal.fire({ title: 'Cancelling...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      for (let id of selectedIds) {
        await api.delete(`/transport/allocations/${id}`);
      }
      Swal.fire({ title: 'Cancelled', text: 'Selected transports have been cancelled.', icon: 'success', timer: 1800, showConfirmButton: false });
      setSelectedIds([]);
      loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to cancel some transports', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePromoteClick = async (alloc) => {
    const oldSession = alloc.session?.name || '—';
    const colId = alloc.college?._id || alloc.college;
    const currentSessionName = alloc.session?.name || '';
    
    let nextSession = null;
    let errorMsg = '';
    
    try {
      const r = await api.get('/sessions', { params: { college: colId } });
      const allSessions = r.data.data || [];
      nextSession = findNextSessionByName(currentSessionName, allSessions);
      
      if (!nextSession) {
        errorMsg = `Next session for "${currentSessionName}" not found. Please create it first.`;
      }
    } catch (err) {
      errorMsg = err.response?.data?.message || 'Failed to fetch sessions';
    }
    
    const newSession = nextSession?.name || oldSession;
    const isSessionNotFound = !nextSession;

    const result = await Swal.fire({
      title: '🔄 Promote Transport',
      html: `
        <div style="text-align:left;font-size:0.92rem;line-height:1.8">
          <p style="margin:0 0 10px">You are promoting transport for:</p>
          <p style="margin:0 0 4px"><strong>Student:</strong> ${alloc.student?.name || '—'} (${alloc.student?.admissionNo || 'N/A'})</p>
          <p style="margin:0 0 4px"><strong>Current Session:</strong> <span style="color:#2563eb">${oldSession}</span></p>
          <p style="margin:0 0 4px"><strong>Promoting To:</strong> <span style="color:${isSessionNotFound ? '#ef4444' : '#16a34a'}">${newSession}${isSessionNotFound ? ' ⚠️ NOT FOUND' : ''}</span></p>
          <p style="margin:0 0 4px"><strong>Current Bus:</strong> ${alloc.bus?.busNumber || '—'} &nbsp;|&nbsp; <strong>Stop:</strong> ${alloc.stop || '—'}</p>
          <hr style="margin:12px 0;border:none;border-top:1px solid #e2e8f0"/>
          ${isSessionNotFound 
            ? `<p style="margin:0;color:#ef4444;font-size:0.85rem">❌ <strong>Session Error:</strong> ${errorMsg}</p>`
            : `<p style="margin:0;color:#64748b;font-size:0.85rem">ℹ️ Bus, route, and stop will remain the same. New dates will be auto-calculated.</p>`
          }
        </div>`,
      icon: isSessionNotFound ? 'error' : 'info',
      showCancelButton: true,
      confirmButtonColor: isSessionNotFound ? '#6b7280' : '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: isSessionNotFound ? 'Close' : 'Promote Now',
      cancelButtonText: 'Cancel',
      width: 520,
      allowOutsideClick: !isSessionNotFound,
      allowEscapeKey: !isSessionNotFound,
    });
    
    if (!result.isConfirmed || !nextSession) return;

    // Directly promote without showing edit form
    setSaving(true);
    try {
      // Auto-calculate new dates: joinDate = old endDate + 1 day, endDate = joinDate + 1 year - 1 day
      let newJoinDate, newEndDate;
      if (alloc.endDate) {
        const end = new Date(alloc.endDate);
        end.setDate(end.getDate() + 1);
        newJoinDate = end.toISOString().split('T')[0];
        const endD = new Date(newJoinDate);
        endD.setFullYear(endD.getFullYear() + 1);
        endD.setDate(endD.getDate() - 1);
        newEndDate = endD.toISOString().split('T')[0];
      } else {
        newJoinDate = new Date().toISOString().split('T')[0];
        const endD = new Date(newJoinDate);
        endD.setFullYear(endD.getFullYear() + 1);
        endD.setDate(endD.getDate() - 1);
        newEndDate = endD.toISOString().split('T')[0];
      }

      // 1. Mark old allocation as completed
      await api.put(`/transport/allocations/${alloc._id}`, { status: 'completed', isActive: false });

      // 2. Create new allocation for next session
      await api.post('/transport/allocations', {
        college:  colId,
        session:  nextSession._id,
        batch:    alloc.batch || '',
        class:    alloc.class?._id || alloc.class,
        student:  alloc.student?._id || alloc.student,
        bus:      alloc.bus?._id || alloc.bus,
        route:    alloc.route?._id || alloc.route,
        stop:     alloc.stop,
        price:    alloc.price,
        joinDate: newJoinDate,
        endDate:  newEndDate,
        status:   'active',
        isActive: true,
      });

      Swal.fire('Success', `Transport promoted to ${nextSession.name}`, 'success');
      loadAllocations();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to promote', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status calculation — check if session is older than active session, then check end date
  const getDaysStatus = (endDateStr, sessionName, collegeId) => {
    if (!endDateStr) return { label: 'Unknown', pillClass: 'unknown', days: 999 };
    
    // Check if this allocation's session is older than the college's active session
    if (collegeId && sessionName && activeSessionsByCollege[collegeId]) {
      const activeSess = activeSessionsByCollege[collegeId];
      const allocYearMatch = sessionName.match(/(\d{4})\D*(\d{2,4})/);
      const activeYearMatch = activeSess.name.match(/(\d{4})\D*(\d{2,4})/);
      
      if (allocYearMatch && activeYearMatch) {
        let allocEndYear = parseInt(allocYearMatch[2]);
        if (allocEndYear < 100) allocEndYear = parseInt(allocYearMatch[1]) + 1;
        
        let activeEndYear = parseInt(activeYearMatch[2]);
        if (activeEndYear < 100) activeEndYear = parseInt(activeYearMatch[1]) + 1;
        
        // If allocation's end year is less than active session's end year, it's from a past session = EXPIRED
        if (allocEndYear < activeEndYear) {
          return { label: 'Expired', pillClass: 'expired', days: -999 };
        }
      }
    }
    
    // Parse only the date part to avoid timezone issues
    const endParts = endDateStr.substring(0, 10).split('-');
    const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((end - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0)   return { label: 'Expired',           pillClass: 'expired', days: diffDays };
    if (diffDays === 0) return { label: 'Today',             pillClass: 'today',   days: 0 };
    if (diffDays <= 3)  return { label: `${diffDays}d left`, pillClass: 'days-3',  days: diffDays };
    if (diffDays <= 7)  return { label: `${diffDays}d left`, pillClass: 'days-7',  days: diffDays };
    if (diffDays <= 30) return { label: `${diffDays}d left`, pillClass: 'days-30', days: diffDays };
    return { label: `${diffDays}d left`, pillClass: 'safe', days: diffDays };
  };

  const displayedAllocations = useMemo(() => {
    return allocations.filter(a => {
      const q = searchQuery.toLowerCase();
      const matchSearch  = (a.student?.name || '').toLowerCase().includes(q) || (a.bus?.busNumber || '').toLowerCase().includes(q);
      const matchCollege = filterCollege ? String(a.college?._id || a.college) === filterCollege : true;
      const matchSession = filterSession ? String(a.session?._id || a.session) === filterSession : true;
      const matchBatch   = filterBatch ? a.batch === filterBatch : true;
      const matchClass   = filterClass ? String(a.class?._id || a.class) === filterClass : true;
      // Status filter - simplified to only show: all, lt30, expired
      let matchStatus = true;
      if (statusFilter !== 'all') {
        const colId = String(a.college?._id || a.college);
        const ds = getDaysStatus(a.endDate, a.session?.name, colId);
        if (statusFilter === 'expired') matchStatus = ds.days < 0;
        else if (statusFilter === 'lt30')   matchStatus = ds.days > 0 && ds.days <= 30;
      }
      return matchSearch && matchCollege && matchSession && matchBatch && matchClass && matchStatus;
    });
  }, [allocations, searchQuery, filterCollege, filterSession, filterBatch, filterClass, statusFilter, activeSessionsByCollege]);

  const grouped = useMemo(() => {
    const g = {};
    displayedAllocations.forEach(a => {
      const key = a.college?.name || colleges.find(c => String(c._id) === String(a.college?._id || a.college))?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(a);
    });
    return g;
  }, [displayedAllocations, colleges]);

  return (
    <div className="student-transport-promotion-container">

      {/* Cancel Modal */}
      {cancelModalAlloc && (
        <CancelModal
          alloc={cancelModalAlloc}
          onClose={() => setCancelModalAlloc(null)}
          onDone={() => { setCancelModalAlloc(null); loadAllocations(); Swal.fire({ title: 'Done', text: 'Transport cancelled successfully.', icon: 'success', timer: 1800, showConfirmButton: false }); }}
        />
      )}

      {/* Filters & Actions */}
      <div className="student-transport-promotion-controls">
        <div className="student-transport-promotion-search-wrapper">
          <FaSearch className="student-transport-promotion-search-icon" />
          <input type="text" className="student-transport-promotion-search-input"
            placeholder="Search Student or Bus..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {!isScoped && colleges.length > 1 && (
          <select className="student-transport-promotion-filter-select" value={filterCollege} onChange={e => { setFilterCollege(e.target.value); setFilterSession(''); setFilterBatch(''); setFilterClass(''); }}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="student-transport-promotion-filter-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {tableFilterOptions.sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {/* Status / Days filter */}
        <select className="student-transport-promotion-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="lt30">Less than 30 days</option>
          <option value="expired">Expired</option>
        </select>

        <select className="student-transport-promotion-filter-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {tableFilterOptions.batches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="student-transport-promotion-filter-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {tableFilterOptions.classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <button
          className="student-transport-promotion-action-btn promote"
          style={{ opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? 'pointer' : 'not-allowed', padding: '10px 14px' }}
          disabled={selectedIds.length === 0}
          onClick={handleBulkPromoteClick}
        >
          Promote
        </button>
        <button
          className="student-transport-promotion-action-btn cancel"
          style={{ opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? 'pointer' : 'not-allowed', padding: '10px 14px' }}
          disabled={selectedIds.length === 0}
          onClick={handleBulkCancelClick}
        >
          Cancel
        </button>
      </div>

      {/* Form */}
      {showPromoteForm && (
        <div className="student-transport-promotion-form">
          <h3 className="student-transport-promotion-form-title">
            🔄 Promote Transport — {promoteAlloc?.student?.name || 'Student'}
            <span style={{fontSize:'0.8rem',fontWeight:400,color:'#64748b',marginLeft:12}}>
              Session: {promoteAlloc?.session?.name || '—'} &rarr; {formSessionName || 'Next Session'}
            </span>
          </h3>
          <div className="student-transport-promotion-form-grid">

            {isAdmin && (
              <div className="student-transport-promotion-form-group">
                <label>College</label>
                <select value={formCollege} onChange={e => setFormCollege(e.target.value)} disabled>
                  <option value="">-- Select College --</option>
                  {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div className="student-transport-promotion-form-group">
              <label>Session (Next Session)</label>
              <input type="text"
                value={formSessionName || (formCollege ? 'Computing next session...' : 'Select college first')}
                readOnly disabled
                style={{ background: formSessionName ? '#f0fdf4' : '#f8fafc', color: formSessionName ? '#16a34a' : '#94a3b8', fontWeight: 600, cursor: 'not-allowed' }}
              />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>Student</label>
              <SearchDropdown placeholder="Select Student..." options={studentOptions} value={formStudent} onChange={setFormStudent} disabled />
            </div>

            <div className="student-transport-promotion-form-group student-transport-promotion-divider">
              <label style={{color:'#3b82f6', fontWeight:700}}>🚌 Update Transport Details</label>
            </div>

            <div className="student-transport-promotion-form-group">
              <label>Bus <span style={{color:'#e74c3c'}}>*</span></label>
              <SearchDropdown placeholder="Select Bus..." options={busOptions} value={formBus} onChange={setFormBus} disabled={!formCollege} />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>Route <span style={{color:'#e74c3c'}}>*</span> <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>(Locked)</span></label>
              <SearchDropdown placeholder="Select Route..." options={routeOptions} value={formRoute} onChange={val => { setFormRoute(val); setFormStop(''); }} disabled={!formCollege || !!formBus} />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>Stop <span style={{color:'#e74c3c'}}>*</span></label>
              <SearchDropdown placeholder="Select Stop..." options={stopOptions} value={formStop} onChange={setFormStop} disabled={!formRoute} />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>Fees (₹) <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>auto</span></label>
              <input type="text" value={calculatedPrice ? `₹ ${calculatedPrice}` : '—'} readOnly style={{ background: '#f0fdf4', fontWeight: 700, color: '#10b981', cursor: 'not-allowed' }} />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>New Join Date <span style={{color:'#e74c3c'}}>*</span></label>
              <input type="date" value={formJoinDate} onChange={e => setFormJoinDate(e.target.value)} />
            </div>

            <div className="student-transport-promotion-form-group">
              <label>New End Date <span style={{color:'#e74c3c'}}>*</span> <span style={{fontSize:'0.72rem', color:'#64748b', fontWeight:400}}>(Auto 1 Year)</span></label>
              <input type="date" value={formEndDate} disabled style={{ background: '#f8fafc', cursor: 'not-allowed', color: '#64748b', fontWeight: 600 }} />
            </div>

          </div>
          <div className="student-transport-promotion-form-buttons">
            <button className="student-transport-promotion-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="student-transport-promotion-btn-confirm" onClick={handlePromoteSubmit} disabled={saving}>
              {saving ? 'Promoting...' : 'Confirm Promotion'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{textAlign:'center', padding:'40px', color:'#64748b'}}>Loading allocations...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{textAlign:'center', padding:'40px', color:'#64748b', background:'#fff', borderRadius:'8px'}}>No active allocations found.</div>
      ) : (
        <div className="student-transport-promotion-grouped-container">
          {Object.entries(grouped).map(([collegeName, items]) => {
            // count by session for header
            const sessionCounts = items.reduce((acc, a) => {
              const sn = a.session?.name || 'Unknown';
              acc[sn] = (acc[sn] || 0) + 1;
              return acc;
            }, {});
            return (
            <div key={collegeName} className="student-transport-promotion-table-group">
              <div className="student-transport-promotion-group-header">
                <span>🏫 {collegeName}</span>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                  {Object.entries(sessionCounts).map(([sn, cnt]) => (
                    <span key={sn} style={{fontSize:'0.78rem',fontWeight:600,background:'#eff6ff',color:'#2563eb',padding:'2px 10px',borderRadius:'8px',border:'1px solid #bfdbfe'}}>
                      {sn}: {cnt} active
                    </span>
                  ))}
                </div>
              </div>
              <div className="student-transport-promotion-table-wrapper">
                <table className="student-transport-promotion-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input 
                          type="checkbox" 
                          style={{ cursor: 'pointer' }}
                          checked={items.length > 0 && items.every(a => selectedIds.includes(a._id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newIds = items.map(a => a._id);
                              setSelectedIds(prev => [...new Set([...prev, ...newIds])]);
                            } else {
                              const itemIds = items.map(a => a._id);
                              setSelectedIds(prev => prev.filter(id => !itemIds.includes(id)));
                            }
                          }}
                        />
                      </th>
                      <th>S.No</th>
                      <th style={{textAlign:'left'}}>Student</th>
                      <th className="student-transport-promotion-hide-sm">Session</th>
                      <th className="student-transport-promotion-hide-sm">Batch</th>
                      <th className="student-transport-promotion-hide-md">Class</th>
                      <th>Bus</th>
                      <th>Stop</th>
                      <th className="student-transport-promotion-hide-sm">Route</th>
                      <th>Fees</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((alloc, i) => {
                      return (
                        <React.Fragment key={alloc._id}>
                          <tr className="student-transport-promotion-main-row">
                            <td>
                              <input 
                                type="checkbox" 
                                style={{ cursor: 'pointer' }}
                                checked={selectedIds.includes(alloc._id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedIds(prev => [...prev, alloc._id]);
                                  else setSelectedIds(prev => prev.filter(id => id !== alloc._id));
                                }}
                              />
                            </td>
                            <td>{i + 1}</td>
                            <td style={{textAlign:'left'}}>
                              <div className="student-transport-promotion-name-wrapper"
                                onClick={() => setExpandedRow(expandedRow === alloc._id ? null : alloc._id)}>
                                <span style={{fontWeight:600}}>{alloc.student?.name || '—'}</span>
                                <button className="student-transport-promotion-expand-btn">
                                  {expandedRow === alloc._id ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                              <div style={{fontSize:'0.72rem', color:'#94a3b8'}}>{alloc.student?.admissionNo || ''}</div>
                            </td>
                            <td className="student-transport-promotion-hide-sm"><span className="student-transport-promotion-badge-session">{alloc.session?.name || '—'}</span></td>
                            <td className="student-transport-promotion-hide-sm">{alloc.batch || '—'}</td>
                            <td className="student-transport-promotion-hide-md">{alloc.class?.name || alloc.class || '—'}</td>
                            <td><span className="student-transport-promotion-bus-badge">{alloc.bus?.busNumber || '—'}</span></td>
                            <td style={{fontWeight:600, color:'#1e293b'}}>{alloc.stop || '—'}</td>
                            <td className="student-transport-promotion-hide-sm" style={{fontSize:'0.82rem', color:'#64748b'}}>{alloc.route?.name || '—'}</td>
                            <td><strong style={{color:'#10b981'}}>₹{alloc.price || 0}</strong></td>
                            <td>
                              <span className={`student-transport-promotion-status-pill ${getDaysStatus(alloc.endDate, alloc.session?.name, String(alloc.college?._id || alloc.college)).pillClass}`}>
                                {getDaysStatus(alloc.endDate, alloc.session?.name, String(alloc.college?._id || alloc.college)).label}
                              </span>
                            </td>
                            <td>
                              <div style={{display:'flex',gap:'6px',flexWrap:'wrap',justifyContent:'center'}}>
                                {(() => {
                                  const colId = String(alloc.college?._id || alloc.college);
                                  const ds = getDaysStatus(alloc.endDate, alloc.session?.name, colId);
                                  const isSelected = selectedIds.includes(alloc._id);
                                  const canPromote = ds.days <= 30 && !isSelected;
                                  const canCancel  = !isSelected;
                                  return (
                                    <>
                                      <button
                                        className="student-transport-promotion-action-btn promote"
                                        disabled={!canPromote}
                                        style={{opacity: canPromote ? 1 : 0.4, cursor: canPromote ? 'pointer' : 'not-allowed'}}
                                        onClick={() => canPromote && handlePromoteClick(alloc)}
                                      >Promote</button>
                                      <button
                                        className="student-transport-promotion-action-btn cancel"
                                        disabled={!canCancel}
                                        style={{opacity: canCancel ? 1 : 0.4, cursor: canCancel ? 'pointer' : 'not-allowed'}}
                                        onClick={() => canCancel && handleCancel(alloc)}
                                      >Cancel</button>
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                          {expandedRow === alloc._id && (
                            <tr className="student-transport-promotion-expanded-row">
                              <td colSpan="12">
                                <div className="student-transport-promotion-expanded-content">
                                  <div className="student-transport-promotion-exp-item student-transport-promotion-exp-sm-only"><span className="student-transport-promotion-exp-label">Session</span><span className="student-transport-promotion-exp-value">{alloc.session?.name || '—'}</span></div>
                                  <div className="student-transport-promotion-exp-item student-transport-promotion-exp-sm-only"><span className="student-transport-promotion-exp-label">Batch</span><span className="student-transport-promotion-exp-value">{alloc.batch || '—'}</span></div>
                                  <div className="student-transport-promotion-exp-item student-transport-promotion-exp-md-only"><span className="student-transport-promotion-exp-label">Class</span><span className="student-transport-promotion-exp-value">{alloc.class?.name || alloc.class || '—'}</span></div>
                                  <div className="student-transport-promotion-exp-item student-transport-promotion-exp-sm-only"><span className="student-transport-promotion-exp-label">Route</span><span className="student-transport-promotion-exp-value">{alloc.route?.name || '—'}</span></div>
                                  {alloc.cancelDate && <div className="student-transport-promotion-exp-item"><span className="student-transport-promotion-exp-label">Cancelled</span><span className="student-transport-promotion-exp-value">{new Date(alloc.cancelDate).toLocaleDateString()}</span></div>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoteTransport;